import {
  MapIcon,
  SearchIcon,
  FilterIcon,
  GitBranchIcon,
  ClipboardListIcon,
  Grid3X3Icon,
  TargetIcon,
  BarChart3Icon,
  HelpCircleIcon,
  TimerIcon,
  ShuffleIcon,
  BookOpenIcon,
  LayersIcon,
  CompassIcon,
} from 'lucide-react';

export const CHECKOUT_URL = 'https://sensoryclinical-en.impultienda.ar/checkout/';

export const images = {
  mainMockup: 'https://cdn.magicpatterns.com/uploads/u9EfENHDqZKoXpUjv2XMZq/SENSORYCLINICAL-EN.png',
  creator: 'https://cdn.magicpatterns.com/uploads/nXDnvoyDj3UnSUjB81ejqY/DR._ELOWEN_VARELLI_-_SENSORY.png',
};

export const routeSteps = [
  { title: 'Define', text: 'Define the functional concern' },
  { title: 'Review', text: 'Review what you already know' },
  { title: 'Investigate', text: 'Identify what still needs investigation' },
  { title: 'Prioritize', text: 'Prioritize the most relevant questions' },
  { title: 'Plan', text: 'Build the next clinical step' },
  { title: 'Act', text: 'Translate the plan into functional action' },
  { title: 'Measure', text: 'Track observable change' },
  { title: 'Review & Adjust', text: 'Review and determine the next route' },
];

export const beforeItems = [
  'You organize your observations but still aren’t sure what to investigate next.',
  'You keep going back through notes, books and courses before deciding on your next step.',
  'You have several possible directions and struggle to prioritize them.',
  'You know the child’s functional concern but aren’t always sure how to translate it into an actionable plan.',
  'You move between evaluation, intervention and documentation without a consistent sequence.',
  'Complex cases can make you feel like you’re starting the reasoning process over again.',
  'You finish one step and immediately wonder: “What should I do now?”',
];

export const afterItems = [
  'You follow a repeatable pathway from an organized case to the next clinical step.',
  'You identify what information needs further investigation.',
  'You prioritize what matters most before moving forward.',
  'You translate clinical reasoning into functional next steps.',
  'You have a structured sequence for planning, documenting and reviewing progress.',
  'You know where you are in the process — and what comes next.',
  'You have a practical clinical route you can return to whenever a case feels complicated.',
];

export const situations = [
  { icon: LayersIcon, text: 'You have already gathered a lot of information about a child, but you’re still unsure what deserves attention first.' },
  { icon: TimerIcon, text: 'You spend too much time deciding what to investigate, document or address next.' },
  { icon: HelpCircleIcon, text: 'You have a working hypothesis, but translating it into a practical clinical sequence feels difficult.' },
  { icon: BookOpenIcon, text: 'You keep going back to books, courses and notes because you don’t have a simple route to follow.' },
  { icon: ShuffleIcon, text: 'You know several things could be contributing to the child’s difficulty, but you’re not sure how to prioritize them.' },
  { icon: CompassIcon, text: 'You finish an evaluation or session thinking: “I know where I am… but I’m not completely sure where to go from here.”' },
];

export const volumes = [
  {
    number: 'Volume I',
    title: 'From Case Clarity to Clinical Direction',
    intro: 'Learn how to take an organized case and determine what needs attention first. You’ll learn how to structure:',
    items: ['The primary functional concern', 'Current information', 'Relevant observations', 'Context', 'Participation impact', 'Existing hypotheses', 'Priority areas', 'Immediate questions'],
    outro: 'Before deciding what to do next, you’ll know exactly where the case currently stands.',
  },
  {
    number: 'Volume II',
    title: 'The Investigation Route',
    intro: 'Stop asking “What else should I look at?” Use a structured pathway for identifying the information that could meaningfully change your understanding of the case. You’ll receive:',
    items: ['Investigation pathways', 'Clinical question prompts', 'Observation priorities', 'Context filters', 'Missing-information checkpoints', 'Comparison tables', 'Decision prompts', '“What would change my hypothesis?” questions'],
    outro: 'Know what you need to investigate before moving forward.',
  },
  {
    number: 'Volume III',
    title: 'Prioritize Before You Plan',
    intro: 'Complex cases rarely have only one relevant factor. This volume helps you organize multiple possibilities without treating everything as equally important. You’ll explore:',
    items: ['Priority filters', 'Functional relevance', 'Contextual factors', 'Sensory considerations', 'Participation impact', 'Immediate vs. later questions', 'What to investigate first', 'What can wait', 'What requires more information'],
    outro: 'Turn “Everything seems important” into “This is what I need to focus on first.”',
  },
  {
    number: 'Volume IV',
    title: 'The Clinical Route Matrix™',
    intro: 'This is where the entire system comes together: Current Information → Question → Investigation → Finding → Interpretation → Next Step. Includes:',
    items: ['Route mapping', 'Decision points', 'Investigation pathways', 'Priority matrices', 'Next-step filters', 'Clinical reasoning prompts', 'Case progression sheets'],
    outro: 'Stop treating every clinical decision as a completely new problem.',
  },
  {
    number: 'Volume V',
    title: 'From Clinical Reasoning to Functional Action',
    intro: 'Once you know what you’re investigating, the next question becomes: “What am I actually trying to change?” You’ll receive frameworks for:',
    items: ['Functional priorities', 'Meaningful activities', 'Goal development', 'Observable outcomes', 'Baseline information', 'Progress indicators', 'Intervention planning', 'Documentation', 'Communication'],
    outro: 'Move from what you noticed → what matters functionally → what you want to change → how you’ll know it changed.',
  },
  {
    number: 'Volume VI',
    title: 'The Complete Sensory Clinical Route™',
    intro: 'Put the entire system together in one repeatable 8-step pathway:',
    items: ['Define the functional concern', 'Review what you already know', 'Identify what still needs investigation', 'Prioritize the most relevant questions', 'Build the next clinical step', 'Translate the plan into functional action', 'Track observable change', 'Review and determine the next route'],
    outro: 'You’ll also learn to recognize when you don’t yet have enough information to move forward — because knowing when to pause is part of good clinical reasoning too.',
  },
];

export const toolkit = [
  { icon: MapIcon, title: 'Clinical Route Maps', text: 'Visual pathways showing where to go next based on the information currently available.' },
  { icon: SearchIcon, title: 'Investigation Checklists', text: 'Structured prompts to help identify what deserves further investigation.' },
  { icon: FilterIcon, title: 'Priority Filters', text: 'Tools for separating what matters now from what can wait.' },
  { icon: GitBranchIcon, title: 'Decision Pathways', text: 'Visual decision trees for navigating common clinical situations.' },
  { icon: ClipboardListIcon, title: 'Next-Step Planners', text: 'Simple worksheets for translating reasoning into an actionable sequence.' },
  { icon: Grid3X3Icon, title: 'Route Matrices', text: 'Tables connecting current information, questions, investigation, findings and next steps.' },
  { icon: TargetIcon, title: 'Functional Goal Templates', text: 'Frameworks for translating clinical priorities into observable functional outcomes.' },
  { icon: BarChart3Icon, title: 'Progress Review Tools', text: 'Practical templates for reviewing what changed and determining what comes next.' },
];

export const bonuses = [
  {
    number: 'Bonus #1',
    title: 'The Clinical Next-Step Question Vault™',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/bfjFT6csTTKLP1tdYoAmZL/BONUS_1_SENSORYCLINICAL-EN.png',
    description: 'A practical collection of questions designed to help you determine what deserves investigation before you move forward.',
    items: ['Observation questions', 'Context questions', 'Functional questions', 'Investigation prompts', 'Missing-information questions', 'Prioritization questions', 'Hypothesis-challenging questions', 'Progress questions'],
    outro: 'Sometimes the most useful clinical tool is simply knowing what question to ask next.',
  },
  {
    number: 'Bonus #2',
    title: 'The 15-Minute Clinical Route Organizer™',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/uUFhm6HDff7Zdq69t8JKN8/BONUS_2_SENSORYCLINICAL-EN.png',
    description: 'A rapid worksheet that takes you through: Current Case → What I Know → What I Need → Priority → Next Step → Functional Goal.',
    items: [],
    outro: 'Designed to help you organize the next clinical direction in approximately 15 minutes.',
  },
  {
    number: 'Bonus #3',
    title: 'The Clinical Decision Checkpoints™',
    value: 27,
    image: 'https://cdn.magicpatterns.com/uploads/fP4JeqrA17S9GiJRRTxr7s/BONUS_3_SENSORYCLINICAL-EN.png',
    description: 'A collection of checkpoints that help you pause before moving forward. You’ll review:',
    items: ['Do I have enough information?', 'What am I assuming?', 'What still needs investigation?', 'What is the functional impact?', 'What could change my current hypothesis?', 'What should happen next?'],
    outro: 'Use these checkpoints whenever a case feels unclear.',
  },
  {
    number: 'Bonus #4',
    title: '10 Sensory Clinical Route Challenges™',
    value: 29,
    image: 'https://cdn.magicpatterns.com/uploads/sanSgy93q52UWs4Ljc6BHo/BONUS_4_SENSORYCLINICAL-EN.png',
    description: 'Ten educational mini-cases designed to help you practice the route. For each case you’ll work through:',
    items: ['What do you know?', 'What is missing?', 'What should you investigate?', 'What should you prioritize?', 'What would you do next?', 'What functional outcome would you track?'],
    outro: 'The purpose isn’t to memorize answers — it’s to train the process of moving from information to action.',
  },
  {
    number: 'Bonus #5',
    title: 'The Clinical Progress & Next-Step Tracker™',
    value: 17,
    image: 'https://cdn.magicpatterns.com/uploads/wru9mit2ehHrRmx9tDkMMv/BONUS_5_SENSORYCLINICAL-EN.png',
    description: 'A practical collection of printable tools for:',
    items: ['Baseline documentation', 'Functional goals', 'Progress tracking', 'Session observations', 'Outcome review', 'Parent communication', 'Clinical reflections', 'Next-step planning'],
    outro: 'So you don’t simply know what happened — you can determine what needs to happen next.',
  },
];

export const testimonials = [
  {
    name: 'Sarah M.',
    role: 'Pediatric Occupational Therapist',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/75eba40c-e81a-4898-8f12-76542e1ffdcd.jpg',
    headline: '“I finally know what to do after organizing the case.”',
    body: 'I already had a way to organize my observations, but I often got stuck on the next step. The Clinical Route gave me a much clearer sequence to follow.',
  },
  {
    name: 'Danielle R.',
    role: 'Occupational Therapist',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/b938f91c-bfc8-4450-a0c9-ef6f87d3cddd.jpg',
    headline: '“It feels like a roadmap instead of another course.”',
    body: 'What I liked most is that I can open the section I need and immediately see what question or step comes next. I don’t have to search through hours of content.',
  },
  {
    name: 'Michael T.',
    role: 'Occupational Therapist',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/420c78e1-8359-4f04-aeef-b054a302349f.jpg',
    headline: '“The prioritization tools changed how I approach complex cases.”',
    body: 'I used to treat every piece of information as equally important. The priority filters helped me think about what actually needs attention first.',
  },
  {
    name: 'Lucía G.',
    role: 'Pediatric Occupational Therapist',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/19be756c-59b5-4731-8643-065979f08ef5.jpg',
    headline: '“I use the route during my planning.”',
    body: 'The next-step worksheets are what I use most. They help me move from “I know something is happening” to a much clearer plan for what I need to investigate or work on.',
  },
  {
    name: 'Grace L.',
    role: 'Occupational Therapist',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/a2a6dd66-3b42-40c6-87ea-f092be549036.jpg',
    headline: '“It complements the Sensory Decision Map perfectly.”',
    body: 'The Decision Map helped me organize my thinking. The Clinical Route helped me answer the next question: “Okay, now what?”',
  },
];

export const creatorTools = ['Visual routes', 'Decision pathways', 'Investigation checkpoints', 'Priority filters', 'Clinical matrices', 'Next-step planners', 'Functional goal frameworks', 'Case challenges', 'Progress tools'];

export const faqs = [
  { q: 'How will I receive the material?', a: 'Immediately after your purchase, you’ll receive instructions to access and download the Sensory Clinical Route™ volumes and all included bonuses in digital format.' },
  { q: 'Is this a course?', a: 'No. The Sensory Clinical Route™ is a practical digital clinical reasoning and organization system. It is designed to be opened, referenced and used while organizing cases rather than watched as a long series of lessons.' },
  { q: 'Who is this for?', a: 'It is designed for Occupational Therapists and Occupational Therapy students who want a more structured way to move from organized case information toward investigation, functional goals and next clinical steps.' },
  { q: 'Do I need previous experience with Sensory Integration?', a: 'Basic familiarity with sensory concepts will help you get the most from the system. The Sensory Clinical Route™ is designed to organize your reasoning process rather than replace foundational professional education.' },
  { q: 'Does this replace standardized assessments?', a: 'No. The Sensory Clinical Route™ is an educational reasoning and organization tool. It does not replace standardized assessments, professional judgment, certification requirements, diagnosis or individualized clinical care.' },
  { q: 'Can I print the materials?', a: 'Yes. The materials are provided digitally and are designed to be used digitally or printed for personal professional use.' },
  { q: 'Is this a one-time payment?', a: 'Yes. You pay USD $27 once. There is no monthly subscription. The price is automatically converted to your local currency at checkout.' },
  { q: 'How long will I have access?', a: 'You receive lifetime access to the purchased digital materials, subject to the applicable purchase terms.' },
  { q: 'What if I change my mind?', a: 'You have a 7-day guarantee. If you decide the system isn’t useful for your professional learning and organization, you can request a refund according to the applicable purchase terms.' },
];

export const recentBuyers = [
  { name: 'Camila S.', country: 'Colombia' },
  { name: 'Valentina R.', country: 'Mexico' },
  { name: 'Lucía M.', country: 'Spain' },
  { name: 'Sofía P.', country: 'Argentina' },
  { name: 'Daniela C.', country: 'Chile' },
  { name: 'Andrea L.', country: 'Peru' },
  { name: 'Mariana F.', country: 'Ecuador' },
  { name: 'Gabriela T.', country: 'Uruguay' },
  { name: 'Carolina V.', country: 'Costa Rica' },
  { name: 'Isabel N.', country: 'Dominican Republic' },
  { name: 'Paula A.', country: 'Guatemala' },
  { name: 'Fernanda O.', country: 'Venezuela' },
  { name: 'Natalia H.', country: 'Puerto Rico' },
  { name: 'Alejandra D.', country: 'Paraguay' },
  { name: 'Mónica B.', country: 'Panama' },
];
